import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, Search, Plus, MapPin, Navigation, Loader2, ChevronRight, Home, Tag, Clock, Camera, Image as ImageIcon, ScanLine, Trash2, Pencil, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { API, axiosInstance, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PriceTrend } from '../components/PriceTrend';

// Reuses Find-2-Day's proven in-memory photo pipeline (browser-image-compression
// + heic2any). The receipt photo is processed in memory and NEVER stored — only
// the structured data the user confirms is saved.
const COMPRESS_OPTS = { maxSizeMB: 5, maxWidthOrHeight: 2000, useWebWorker: true, fileType: 'image/jpeg', initialQuality: 0.82 };
const heicToJpeg = async (file) => {
  const heic2any = (await import('heic2any')).default;
  const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
  const out = Array.isArray(blob) ? blob[0] : blob;
  return new File([out], (file.name || 'receipt').replace(/\.hei[cf]$/i, '.jpg'), { type: 'image/jpeg' });
};
const isHeic = (file) => {
  const n = (file.name || '').toLowerCase(); const ty = (file.type || '').toLowerCase();
  return n.endsWith('.heic') || n.endsWith('.heif') || ty === 'image/heic' || ty === 'image/heif';
};

// Home & Local Prices (Stage 1). Separate section ALONGSIDE grocery Price-2-Day.
// Reuses the shared town-distance + freshness engine via /api/local/* endpoints.

const freshBadge = (f) => {
  const l = (f && f.label) || 'Unknown';
  const cls = l === 'Recent' ? 'bg-emerald-100 text-emerald-700'
    : l === 'Older' ? 'bg-amber-100 text-amber-700'
    : l === 'Historical' ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-500';
  return { cls, days: f && f.days };
};

const money = (v, c = 'EUR') => {
  if (v === null || v === undefined) return '—';
  const sym = c === 'EUR' ? '\u20ac' : (c + ' ');
  return `${sym}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

export default function LocalPrices() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [taxonomy, setTaxonomy] = useState([]);
  const [towns, setTowns] = useState([]);
  const [view, setView] = useState('landing'); // landing | subcats | results | add | search | detail
  const [activeCat, setActiveCat] = useState(null);
  const [activeSub, setActiveSub] = useState(null);
  const [town, setTown] = useState(() => localStorage.getItem('f2d_price_town') || '');
  const [rows, setRows] = useState([]);
  const [distanceEnabled, setDistanceEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('nearest'); // nearest | cheapest
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [busyDel, setBusyDel] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [q, setQ] = useState('');
  const [searched, setSearched] = useState(false);

  const cl = (id) => t(`local.cat.${id}`, id);
  const sl = (id) => t(`local.sub.${id}`, id);
  const ul = (u) => t(`local.unit.${u}`, u);

  useEffect(() => {
    axiosInstance.get('/local/taxonomy').then((r) => setTaxonomy(r.data?.categories || [])).catch(() => {});
    axiosInstance.get('/local/towns').then((r) => setTowns(r.data?.towns || [])).catch(() => {});
  }, []);

  const fetchRows = useCallback(async (params) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/local/prices', { params: { ...params, fromTown: town || '' } });
      setRows(res.data?.results || []);
      setDistanceEnabled(!!res.data?.distanceEnabled);
    } catch (e) {
      toast.error(t('local.loadError', 'Could not load local prices.'));
      setRows([]);
    } finally { setLoading(false); }
  }, [town, t]);

  const openCategory = (cat) => { setActiveCat(cat); setView('subcats'); };
  const openSub = (sub) => { setActiveSub(sub); setMode(town ? 'nearest' : 'cheapest'); setView('results'); fetchRows({ category: activeCat.id, subcategory: sub.id, sort: town ? 'nearest' : 'recent' }); };
  const changeTown = (v) => {
    setTown(v);
    if (v) localStorage.setItem('f2d_price_town', v); else localStorage.removeItem('f2d_price_town');
    if (view === 'results' && activeSub) fetchRows({ category: activeCat.id, subcategory: activeSub.id, sort: mode });
    if (view === 'search' && searched) fetchRows({ q, sort: mode });
  };
  const runSearch = () => {
    if ((q || '').trim().length < 2) { toast.error(t('local.searchMin', 'Type at least 2 letters.')); return; }
    setSearched(true); setMode(town ? 'nearest' : 'recent'); fetchRows({ q: q.trim(), sort: town ? 'nearest' : 'recent' });
  };

  const refreshRows = () => {
    if (activeSub && activeCat) fetchRows({ category: activeCat.id, subcategory: activeSub.id, sort: mode });
    else if (searched) fetchRows({ q, sort: mode });
  };
  const doDeleteDetail = async () => {
    if (!detail) return;
    setBusyDel(true);
    try {
      await axiosInstance.delete(`/local/prices/${detail.id}`);
      toast.success(t('local.deleted', 'Price deleted'));
      setConfirmDel(false); setDetail(null); setView('results'); refreshRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('local.deleteError', 'Could not delete the price.'));
    } finally { setBusyDel(false); }
  };

  // Load price history whenever the detail view opens for a price.
  useEffect(() => {
    if (view !== 'detail' || !detail?.id) { setHistory(null); return; }
    let active = true;
    setHistory(null); setHistoryLoading(true);
    axiosInstance.get(`/local/prices/${detail.id}/history`)
      .then((res) => { if (active) setHistory(res.data?.observations || []); })
      .catch(() => { if (active) setHistory([]); })
      .finally(() => { if (active) setHistoryLoading(false); });
    return () => { active = false; };
  }, [view, detail?.id]); // eslint-disable-line

  // group rows by unit family; within each family compute cheapest + nearest
  const groups = useMemo(() => {
    const byFam = {};
    rows.forEach((r) => { (byFam[r.unitFamily] = byFam[r.unitFamily] || []).push(r); });
    const out = Object.entries(byFam).map(([fam, list]) => {
      const priced = list.filter((r) => r.perBase);
      const minBase = priced.length ? Math.min(...priced.map((r) => r.perBase.value)) : null;
      const withDist = list.filter((r) => r.distanceKm != null);
      const minDist = withDist.length ? Math.min(...withDist.map((r) => r.distanceKm)) : null;
      const sorted = list.slice().sort((a, b) => {
        if (mode === 'cheapest') return (a.perBase?.value ?? Infinity) - (b.perBase?.value ?? Infinity);
        return (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity);
      });
      const label = fam.startsWith('count:') ? t('local.perUnitFamily', { unit: ul(fam.split(':')[1]), defaultValue: 'Compared per {{unit}}' })
        : t(`local.family.${fam}`, fam);
      const countUnit = fam.startsWith('count:');
      return { fam, label, countUnit, minBase, minDist, rows: sorted };
    });
    // mass/volume/area/length first, count families after
    out.sort((a, b) => (a.countUnit === b.countUnit) ? 0 : (a.countUnit ? 1 : -1));
    return out;
  }, [rows, mode]); // eslint-disable-line

  const dirUrl = (r) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${r.shopName || ''}${r.town ? `, ${r.town}` : ''}`)}`;

  // ---------- render helpers ----------
  const TownSelect = ({ testId }) => (
    <select value={town} onChange={(e) => changeTown(e.target.value)} className="w-full h-10 px-3 rounded-xl border bg-white text-sm" data-testid={testId}>
      <option value="">{t('local.pickTown', 'Select your town to see distances')}</option>
      {towns.map((tn) => <option key={tn} value={tn}>{tn}</option>)}
    </select>
  );

  const PriceCard = ({ r, gi, ri, highlight }) => {
    const fb = freshBadge(r.freshness);
    return (
      <div className={`bg-white border rounded-2xl p-3 mb-2.5 ${highlight ? 'border-indigo-500' : 'border-gray-200'}`} data-testid={`local-result-${gi}-${ri}`} onClick={() => { setDetail(r); setEditing(false); setConfirmDel(false); setView('detail'); }}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-extrabold text-slate-900 text-sm truncate">
              {r.productName}
              {highlight === 'cheapest' && <span className="ml-1.5 text-[9px] font-black bg-indigo-600 text-white rounded px-1.5 py-0.5" data-testid={`local-pick-cheapest-${gi}`}>{t('local.cheapest', 'CHEAPEST')}</span>}
              {highlight === 'nearest' && <span className="ml-1.5 text-[9px] font-black bg-indigo-600 text-white rounded px-1.5 py-0.5" data-testid={`local-pick-nearest-${gi}`}>{t('local.nearest', 'NEAREST')}</span>}
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-lg font-black text-indigo-600">{money(r.pricePerUnit, r.currency)}</div>
            <div className="text-[10px] font-bold text-slate-500">{t('local.per', 'per')} {ul(r.unit)}</div>
          </div>
        </div>
        <div className="text-[11px] text-slate-600 mt-1 flex flex-wrap gap-x-3 gap-y-1 items-center">
          <span>🏪 {r.shopName}{r.town ? ` · ${r.town}` : ''}</span>
          <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${fb.cls}`}>{t(`local.fresh.${(r.freshness?.label || 'Unknown').toLowerCase()}`, r.freshness?.label)}{fb.days != null ? ` · ${fb.days}d` : ''}</span>
        </div>
        <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-3">
          {r.locationStatus === 'ok' && r.distanceKm != null
            ? <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />~{r.distanceKm} km <span className="text-slate-400">({t('local.approx', 'Approx.')})</span></span>
            : distanceEnabled ? <span className="inline-flex items-center gap-1 text-slate-400"><MapPin className="h-3 w-3" />{t('local.locUnavailable', 'Location not yet available')}</span> : null}
          {r.perBase && <span className="text-[10px] font-bold text-indigo-700 bg-indigo-50 rounded px-1.5 py-0.5">{money(r.perBase.value, r.currency)}{r.perBase.label.replace('\u20ac', '')}</span>}
        </div>
      </div>
    );
  };

  const ResultsBody = () => (
    <>
      <div className="mb-3"><TownSelect testId="local-town-select" /></div>
      <div className="grid grid-cols-2 rounded-full bg-gray-200 p-1 text-[13px] font-semibold mb-3">
        {[['cheapest', t('local.cheapest', 'Cheapest')], ['nearest', t('local.nearest', 'Nearest')]].map(([k, lab]) => (
          <button key={k} onClick={() => setMode(k)} className={`rounded-full py-1.5 transition-colors ${mode === k ? 'bg-white shadow text-indigo-700' : 'text-gray-500'}`} data-testid={`local-mode-${k}`}>{lab}</button>
        ))}
      </div>
      {loading ? <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-500" /></div>
        : rows.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-500" data-testid="local-empty">
            <p>{t('local.noPrices', 'No prices yet for this. Be the first to add one.')}</p>
            <Button className="mt-3" onClick={() => setView('add')} data-testid="local-empty-add"><Plus className="h-4 w-4 mr-1" />{t('local.addPrice', 'Add a Price')}</Button>
          </div>
        ) : groups.map((g, gi) => (
          <div key={g.fam} className="mb-4" data-testid={`local-family-${gi}`}>
            <div className="text-[12px] font-bold text-slate-500 mb-1.5">
              {g.label}{g.countUnit ? <span className="ml-1 font-normal text-amber-600">· {t('local.sizesVary', 'pack sizes may vary')}</span> : null}
            </div>
            {g.rows.map((r, ri) => {
              let hl = null;
              if (mode === 'cheapest' && r.perBase && g.minBase != null && r.perBase.value === g.minBase) hl = 'cheapest';
              else if (mode === 'nearest' && r.distanceKm != null && g.minDist != null && r.distanceKm === g.minDist) hl = 'nearest';
              return <PriceCard key={r.id} r={r} gi={gi} ri={ri} highlight={hl} />;
            })}
          </div>
        ))}
      <p className="text-[10px] text-gray-400 mt-2 leading-relaxed">{t('local.disclaimer', 'Prices are community-provided and may change or vary between suppliers. Always check the current price before buying.')}</p>
    </>
  );

  // ---------- Add a Price ----------
  const AddForm = () => {
    const [f, setF] = useState({
      categoryId: activeCat?.id || 'heating', subCategoryId: activeSub?.id || '',
      productName: '', price: '', unit: '', quantity: '1',
      shopName: '', town: town || '', observedDate: new Date().toISOString().slice(0, 10),
      brand: '', packContainsMag: '', packContainsUnit: '', grade: '', includesDelivery: false, note: '',
    });
    const [more, setMore] = useState(false);
    const [saving, setSaving] = useState(false);
    const cat = taxonomy.find((c) => c.id === f.categoryId);
    const subs = cat?.subs || [];
    const sub = subs.find((s) => s.id === f.subCategoryId);
    const unitOpts = sub?.units || [];
    const set = (k, v) => setF((p) => {
      const n = { ...p, [k]: v };
      if (k === 'categoryId') { n.subCategoryId = ''; n.unit = ''; }
      if (k === 'subCategoryId') { const s = (taxonomy.find((c) => c.id === n.categoryId)?.subs || []).find((x) => x.id === v); n.unit = s?.units?.[0] || ''; }
      return n;
    });
    const submit = async () => {
      if (!f.subCategoryId || !f.productName.trim() || !f.price || !f.unit || !f.shopName.trim() || !f.town) {
        toast.error(t('local.addRequired', 'Please fill category, subcategory, product, price, unit, shop and town.')); return;
      }
      setSaving(true);
      try {
        const body = {
          categoryId: f.categoryId, subCategoryId: f.subCategoryId, productName: f.productName.trim(),
          price: parseFloat(f.price), unit: f.unit, quantity: parseFloat(f.quantity || '1'),
          shopName: f.shopName.trim(), town: f.town, observedDate: f.observedDate,
          brand: f.brand || null, grade: f.grade || null,
          includesDelivery: f.includesDelivery || null, note: f.note || null,
          packContainsMag: f.packContainsMag ? parseFloat(f.packContainsMag) : null,
          packContainsUnit: f.packContainsUnit || null,
        };
        await axiosInstance.post('/local/prices', body);
        toast.success(t('local.addSaved', 'Price added. Thank you!'));
        setActiveCat(taxonomy.find((c) => c.id === f.categoryId));
        setActiveSub(sub);
        setView('results');
        fetchRows({ category: f.categoryId, subcategory: f.subCategoryId, sort: town ? 'nearest' : 'recent' });
      } catch (e) {
        toast.error(e?.response?.data?.detail || t('local.addError', 'Could not add the price.'));
      } finally { setSaving(false); }
    };
    const Lbl = ({ children }) => <label className="text-[11px] font-semibold text-slate-600 block mb-1 mt-2.5">{children}</label>;
    const selCls = 'w-full h-10 px-3 rounded-xl border bg-white text-sm';
    return (
      <div className="pb-6" data-testid="local-add-form">
        <Lbl>{t('local.fCategory', 'Category')}</Lbl>
        <select value={f.categoryId} onChange={(e) => set('categoryId', e.target.value)} className={selCls} data-testid="local-add-category">
          {taxonomy.map((c) => <option key={c.id} value={c.id}>{cl(c.id)}</option>)}
        </select>
        <Lbl>{t('local.fSubcategory', 'Subcategory')}</Lbl>
        <select value={f.subCategoryId} onChange={(e) => set('subCategoryId', e.target.value)} className={selCls} data-testid="local-add-subcategory">
          <option value="">{t('local.choose', 'Choose…')}</option>
          {subs.map((s) => <option key={s.id} value={s.id}>{sl(s.id)}</option>)}
        </select>
        <Lbl>{t('local.fProduct', 'Product name')}</Lbl>
        <Input value={f.productName} onChange={(e) => set('productName', e.target.value)} placeholder={t('local.fProductPh', 'e.g. Hardwood logs')} data-testid="local-add-product" />
        <div className="grid grid-cols-3 gap-2">
          <div><Lbl>{t('local.fPrice', 'Price (€)')}</Lbl><Input type="number" step="0.01" min="0" value={f.price} onChange={(e) => set('price', e.target.value)} data-testid="local-add-price" /></div>
          <div><Lbl>{t('local.fUnit', 'Unit')}</Lbl>
            <select value={f.unit} onChange={(e) => set('unit', e.target.value)} className={selCls} data-testid="local-add-unit">
              <option value="">{t('local.choose', 'Choose…')}</option>
              {unitOpts.map((u) => <option key={u} value={u}>{ul(u)}</option>)}
            </select></div>
          <div><Lbl>{t('local.fQty', 'Qty')}</Lbl><Input type="number" step="0.01" min="0" value={f.quantity} onChange={(e) => set('quantity', e.target.value)} data-testid="local-add-qty" /></div>
        </div>
        <Lbl>{t('local.fShop', 'Shop / Supplier')}</Lbl>
        <Input value={f.shopName} onChange={(e) => set('shopName', e.target.value)} placeholder={t('local.fShopPh', 'e.g. Almanzora Biomasa')} data-testid="local-add-shop" />
        <div className="grid grid-cols-2 gap-2">
          <div><Lbl>{t('local.fTown', 'Town')}</Lbl>
            <select value={f.town} onChange={(e) => set('town', e.target.value)} className={selCls} data-testid="local-add-town">
              <option value="">{t('local.choose', 'Choose…')}</option>
              {towns.map((tn) => <option key={tn} value={tn}>{tn}</option>)}
            </select></div>
          <div><Lbl>{t('local.fDate', 'Date')}</Lbl><Input type="date" value={f.observedDate} max={new Date().toISOString().slice(0, 10)} onChange={(e) => set('observedDate', e.target.value)} data-testid="local-add-date" /></div>
        </div>
        <button onClick={() => setMore(!more)} className="text-[12px] text-indigo-600 font-semibold mt-3" data-testid="local-add-more">▸ {t('local.moreDetails', 'More details (brand · pack contains · grade · delivery · note)')}</button>
        {more && (
          <div className="mt-2 space-y-1">
            <Lbl>{t('local.fBrand', 'Brand')}</Lbl><Input value={f.brand} onChange={(e) => set('brand', e.target.value)} data-testid="local-add-brand" />
            <div className="grid grid-cols-2 gap-2">
              <div><Lbl>{t('local.fPackMag', 'Pack contains (size)')}</Lbl><Input type="number" step="0.01" min="0" value={f.packContainsMag} onChange={(e) => set('packContainsMag', e.target.value)} placeholder="25" data-testid="local-add-packmag" /></div>
              <div><Lbl>{t('local.fPackUnit', 'of unit')}</Lbl>
                <select value={f.packContainsUnit} onChange={(e) => set('packContainsUnit', e.target.value)} className={selCls} data-testid="local-add-packunit">
                  <option value="">—</option>{['kg', 'g', 'litre', 'ml', 'm2', 'm3', 'item'].map((u) => <option key={u} value={u}>{ul(u)}</option>)}
                </select></div>
            </div>
            <Lbl>{t('local.fGrade', 'Grade / spec')}</Lbl><Input value={f.grade} onChange={(e) => set('grade', e.target.value)} placeholder={t('local.fGradePh', 'e.g. hardwood / softwood')} data-testid="local-add-grade" />
            <label className="flex items-center gap-2 mt-2 text-sm text-slate-700"><input type="checkbox" checked={f.includesDelivery} onChange={(e) => set('includesDelivery', e.target.checked)} data-testid="local-add-delivery" />{t('local.fDelivery', 'Price includes delivery')}</label>
            <Lbl>{t('local.fNote', 'Note')}</Lbl><Input value={f.note} onChange={(e) => set('note', e.target.value)} data-testid="local-add-note" />
          </div>
        )}
        <Button className="w-full mt-4 h-11 bg-indigo-600 hover:bg-indigo-700" onClick={submit} disabled={saving} data-testid="local-add-submit">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('local.savePrice', 'Save price')}
        </Button>
      </div>
    );
  };

  // ---------- Edit a saved Price (owner/admin) ----------
  const EditForm = () => {
    const d = detail || {};
    const pc = d.packContains || {};
    const [f, setF] = useState({
      categoryId: d.categoryId || 'heating', subCategoryId: d.subCategoryId || '',
      productName: d.productName || '', price: d.price ?? '', unit: d.unit || '', quantity: String(d.quantity ?? '1'),
      shopName: d.shopName || '', shopBranch: d.shopBranch || '', town: d.town || town || '',
      observedDate: (d.observedDate || new Date().toISOString().slice(0, 10)).slice(0, 10),
      brand: d.brand || '', packContainsMag: pc.mag != null ? String(pc.mag) : '', packContainsUnit: pc.unit || '',
      grade: d.grade || '', includesDelivery: !!d.includesDelivery, note: d.note || '',
    });
    const [saving, setSaving] = useState(false);
    const cat = taxonomy.find((c) => c.id === f.categoryId);
    const subs = cat?.subs || [];
    const sub = subs.find((s) => s.id === f.subCategoryId);
    const unitOpts = sub?.units || [];
    const set = (k, v) => setF((p) => {
      const n = { ...p, [k]: v };
      if (k === 'categoryId') { n.subCategoryId = ''; n.unit = ''; }
      if (k === 'subCategoryId') { const s = (taxonomy.find((c) => c.id === n.categoryId)?.subs || []).find((x) => x.id === v); n.unit = s?.units?.[0] || ''; }
      return n;
    });
    const save = async () => {
      if (!f.subCategoryId || !f.productName.trim() || !f.price || !f.unit || !f.shopName.trim() || !f.town) {
        toast.error(t('local.addRequired', 'Please fill category, subcategory, product, price, unit, shop and town.')); return;
      }
      setSaving(true);
      try {
        const body = {
          categoryId: f.categoryId, subCategoryId: f.subCategoryId, productName: f.productName.trim(),
          price: parseFloat(f.price), unit: f.unit, quantity: parseFloat(f.quantity || '1'),
          shopName: f.shopName.trim(), shopBranch: f.shopBranch.trim() || null, town: f.town, observedDate: f.observedDate,
          brand: f.brand.trim() || null, grade: f.grade.trim() || null,
          includesDelivery: f.includesDelivery, note: f.note.trim() || null,
          packContainsMag: f.packContainsMag ? parseFloat(f.packContainsMag) : null,
          packContainsUnit: f.packContainsUnit || null,
        };
        const res = await axiosInstance.put(`/local/prices/${d.id}`, body);
        toast.success(t('local.editSaved', 'Price updated'));
        setDetail(res.data?.price || null);
        setEditing(false);
        refreshRows();
      } catch (e) {
        toast.error(e?.response?.data?.detail || t('local.editError', 'Could not update the price.'));
      } finally { setSaving(false); }
    };
    const Lbl = ({ children }) => <label className="text-[11px] font-semibold text-slate-600 block mb-1 mt-2.5">{children}</label>;
    const selCls = 'w-full h-10 px-3 rounded-xl border bg-white text-sm';
    return (
      <div className="pb-6" data-testid="local-edit-form">
        <h3 className="text-base font-bold text-slate-900 mb-1">{t('local.editTitle', 'Edit price')}</h3>
        <Lbl>{t('local.fCategory', 'Category')}</Lbl>
        <select value={f.categoryId} onChange={(e) => set('categoryId', e.target.value)} className={selCls} data-testid="local-edit-category">
          {taxonomy.map((c) => <option key={c.id} value={c.id}>{cl(c.id)}</option>)}
        </select>
        <Lbl>{t('local.fSubcategory', 'Subcategory')}</Lbl>
        <select value={f.subCategoryId} onChange={(e) => set('subCategoryId', e.target.value)} className={selCls} data-testid="local-edit-subcategory">
          <option value="">{t('local.choose', 'Choose…')}</option>
          {subs.map((s) => <option key={s.id} value={s.id}>{sl(s.id)}</option>)}
        </select>
        <Lbl>{t('local.fProduct', 'Product name')}</Lbl>
        <Input value={f.productName} onChange={(e) => set('productName', e.target.value)} data-testid="local-edit-product" />
        <div className="grid grid-cols-3 gap-2">
          <div><Lbl>{t('local.fPrice', 'Price (€)')}</Lbl><Input type="number" step="0.01" min="0" value={f.price} onChange={(e) => set('price', e.target.value)} data-testid="local-edit-price" /></div>
          <div><Lbl>{t('local.fUnit', 'Unit')}</Lbl>
            <select value={f.unit} onChange={(e) => set('unit', e.target.value)} className={selCls} data-testid="local-edit-unit">
              <option value="">{t('local.choose', 'Choose…')}</option>
              {unitOpts.map((u) => <option key={u} value={u}>{ul(u)}</option>)}
            </select></div>
          <div><Lbl>{t('local.fQty', 'Qty')}</Lbl><Input type="number" step="0.01" min="0" value={f.quantity} onChange={(e) => set('quantity', e.target.value)} data-testid="local-edit-qty" /></div>
        </div>
        <Lbl>{t('local.fShop', 'Shop / Supplier')}</Lbl>
        <Input value={f.shopName} onChange={(e) => set('shopName', e.target.value)} data-testid="local-edit-shop" />
        <div className="grid grid-cols-2 gap-2">
          <div><Lbl>{t('local.fTown', 'Town')}</Lbl>
            <select value={f.town} onChange={(e) => set('town', e.target.value)} className={selCls} data-testid="local-edit-town">
              <option value="">{t('local.choose', 'Choose…')}</option>
              {towns.map((tn) => <option key={tn} value={tn}>{tn}</option>)}
            </select></div>
          <div><Lbl>{t('local.fDate', 'Date')}</Lbl><Input type="date" value={f.observedDate} max={new Date().toISOString().slice(0, 10)} onChange={(e) => set('observedDate', e.target.value)} data-testid="local-edit-date" /></div>
        </div>
        <Lbl>{t('local.fBrand', 'Brand')}</Lbl><Input value={f.brand} onChange={(e) => set('brand', e.target.value)} data-testid="local-edit-brand" />
        <div className="grid grid-cols-2 gap-2">
          <div><Lbl>{t('local.fPackMag', 'Pack contains (size)')}</Lbl><Input type="number" step="0.01" min="0" value={f.packContainsMag} onChange={(e) => set('packContainsMag', e.target.value)} data-testid="local-edit-packmag" /></div>
          <div><Lbl>{t('local.fPackUnit', 'of unit')}</Lbl>
            <select value={f.packContainsUnit} onChange={(e) => set('packContainsUnit', e.target.value)} className={selCls} data-testid="local-edit-packunit">
              <option value="">—</option>{['kg', 'g', 'litre', 'ml', 'm2', 'm3', 'item'].map((u) => <option key={u} value={u}>{ul(u)}</option>)}
            </select></div>
        </div>
        <Lbl>{t('local.fGrade', 'Grade / spec')}</Lbl><Input value={f.grade} onChange={(e) => set('grade', e.target.value)} data-testid="local-edit-grade" />
        <label className="flex items-center gap-2 mt-2 text-sm text-slate-700"><input type="checkbox" checked={f.includesDelivery} onChange={(e) => set('includesDelivery', e.target.checked)} data-testid="local-edit-delivery" />{t('local.fDelivery', 'Price includes delivery')}</label>
        <Lbl>{t('local.fNote', 'Note')}</Lbl>
        <textarea value={f.note} maxLength={500} rows={3} onChange={(e) => set('note', e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" data-testid="local-edit-note" />
        <p className="text-[10px] text-gray-400 text-right">{(f.note || '').length}/500</p>
        <div className="flex gap-2 mt-3">
          <Button className="flex-1 h-11 bg-indigo-600 hover:bg-indigo-700" onClick={save} disabled={saving} data-testid="local-edit-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('local.saveChanges', 'Save changes')}
          </Button>
          <Button variant="outline" className="h-11" onClick={() => setEditing(false)} data-testid="local-edit-cancel">{t('local.cancel', 'Cancel')}</Button>
        </div>
      </div>
    );
  };

  // ---------- Scan Receipt ----------
  const ScanFlow = () => {
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [sform, setSform] = useState(null); // {shopName, shopBranch, town, observedDate, currency, includesDelivery, items:[]}
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState(null);
    const camRef = React.useRef(null);
    const galRef = React.useRef(null);

    const handleFile = async (file) => {
      if (!file) return;
      setBusy(true); setResult(null);
      try {
        const imageCompression = (await import('browser-image-compression')).default;
        let working = file;
        if (isHeic(file)) working = await heicToJpeg(file);
        const compressed = await imageCompression(working, COMPRESS_OPTS);
        setPreview(URL.createObjectURL(compressed));
        const name = (compressed.name || 'receipt.jpg').toLowerCase();
        const fd = new FormData();
        fd.append('file', compressed, (name.endsWith('.jpg') || name.endsWith('.jpeg')) ? name : `${name}.jpg`);
        const res = await axiosInstance.post('/local/scan', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const ex = res.data?.extracted;
        if (!ex || !(ex.items || []).length) { toast.error(t('local.scanNone', 'No items could be read. Try a clearer photo.')); setBusy(false); return; }
        const today = new Date().toISOString().slice(0, 10);
        const exDate = (ex.purchaseDate && /^\d{4}-\d{2}-\d{2}$/.test(ex.purchaseDate) && ex.purchaseDate <= today) ? ex.purchaseDate : today;
        setSform({
          shopName: ex.shopName || '', shopBranch: '', town: town || '',
          observedDate: exDate,
          currency: ex.currency || 'EUR', includesDelivery: false,
          items: (ex.items || []).map((it) => ({
            productName: it.productName || it.description || '',
            categoryId: it.suggestedCategory || '',
            subCategoryId: it.suggestedSubcategory || '',
            unit: it.unit || '',
            quantity: it.quantity || 1,
            price: it.price != null ? it.price : '',
            brand: '',
            packContainsMag: it.packContainsMag != null ? it.packContainsMag : '',
            packContainsUnit: it.packContainsUnit || '',
            needsReview: !!it.needsReview, confidence: it.confidence || 'low',
          })),
        });
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        const msg = (typeof detail === 'string' && detail) ? detail
          : (status === 401 || status === 403) ? t('local.signInToScan', 'Please sign in to scan supplier receipts.')
          : t('local.scanUnreadable', "We couldn't read this receipt clearly. Please try another photo with the whole receipt visible and good lighting.");
        toast.error(msg);
      } finally { setBusy(false); }
    };

    const setItem = (i, k, v) => setSform((p) => {
      const items = p.items.slice();
      const row = { ...items[i], [k]: v };
      if (k === 'categoryId') { row.subCategoryId = ''; row.unit = ''; }
      if (k === 'subCategoryId') { const s = (taxonomy.find((c) => c.id === row.categoryId)?.subs || []).find((x) => x.id === v); row.unit = s?.units?.[0] || ''; }
      // recompute review flag
      row.needsReview = !row.categoryId || !row.subCategoryId || !row.unit;
      items[i] = row; return { ...p, items };
    });
    const removeItem = (i) => setSform((p) => ({ ...p, items: p.items.filter((_, x) => x !== i) }));

    const anyReview = sform && sform.items.some((it) => it.needsReview || !it.price);
    const save = async () => {
      if (!sform.shopName.trim() || !sform.town) { toast.error(t('local.scanShopTown', 'Please enter the shop and town.')); return; }
      const today = new Date().toISOString().slice(0, 10);
      if (sform.observedDate > today) { toast.error(t('local.futureDate', 'The receipt date cannot be in the future.')); return; }
      const clean = sform.items.filter((it) => it.productName.trim() && it.categoryId && it.subCategoryId && it.unit && parseFloat(it.price) > 0);
      if (!clean.length) { toast.error(t('local.scanNoValid', 'Complete category, unit and price for at least one item.')); return; }
      setSaving(true);
      try {
        const body = {
          shopName: sform.shopName.trim(), shopBranch: sform.shopBranch || null, town: sform.town,
          observedDate: sform.observedDate, currency: sform.currency, includesDelivery: sform.includesDelivery || null,
          items: clean.map((it) => ({
            categoryId: it.categoryId, subCategoryId: it.subCategoryId, productName: it.productName.trim(),
            price: parseFloat(it.price), unit: it.unit, quantity: parseFloat(it.quantity || '1'),
            brand: (it.brand && it.brand.trim()) || null,
            packContainsMag: it.packContainsMag ? parseFloat(it.packContainsMag) : null,
            packContainsUnit: it.packContainsUnit || null,
          })),
        };
        const res = await axiosInstance.post('/local/prices/bulk', body);
        setResult(res.data);
        toast.success(t('local.scanSaved', { saved: res.data.saved, defaultValue: 'Saved {{saved}} price(s). Thank you!' }));
      } catch (e) {
        toast.error(e?.response?.data?.detail || t('local.addError', 'Could not save.'));
      } finally { setSaving(false); }
    };

    const selCls = 'w-full h-9 px-2 rounded-lg border bg-white text-[13px]';
    const cat = (id) => taxonomy.find((c) => c.id === id);

    if (!user) return (
      <div className="text-center py-12" data-testid="local-scan-signin">
        <div className="mx-auto w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center"><ScanLine className="h-7 w-7 text-indigo-600" /></div>
        <p className="mt-3 font-bold text-slate-800">{t('local.scanTitle', 'Scan a supplier receipt')}</p>
        <p className="text-[13px] text-slate-500 mt-1 px-6">{t('local.signInToScan', 'Please sign in to scan supplier receipts.')}</p>
        <Button className="mt-4 bg-indigo-600" onClick={() => navigate('/signin')} data-testid="local-scan-signin-btn">{t('local.signIn', 'Sign in')}</Button>
      </div>
    );

    if (result) return (
      <div className="text-center py-10" data-testid="local-scan-result">
        <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
        <p className="mt-3 font-bold text-slate-800">{t('local.scanSavedTitle', 'Prices saved')}</p>
        <p className="text-sm text-slate-500 mt-1">{t('local.scanSavedCount', { saved: result.saved, skipped: result.skippedDuplicates, defaultValue: '{{saved}} saved · {{skipped}} duplicate(s) skipped' })}</p>
        <Button className="mt-4 bg-indigo-600" onClick={() => { setSform(null); setPreview(null); setResult(null); }} data-testid="local-scan-again"><Camera className="h-4 w-4 mr-1" />{t('local.scanAnother', 'Scan another')}</Button>
      </div>
    );

    if (!sform) return (
      <div className="text-center py-6" data-testid="local-scan-start">
        <div className="mx-auto w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center">
          {busy ? <Loader2 className="h-7 w-7 text-indigo-600 animate-spin" /> : <ScanLine className="h-7 w-7 text-indigo-600" />}
        </div>
        <h2 className="text-base font-bold text-slate-900 mt-3">{t('local.scanTitle', 'Scan a supplier receipt')}</h2>
        <p className="text-[13px] text-slate-500 mt-1 px-4">{t('local.scanBlurb', 'Photograph or upload a receipt from a fuel, DIY, builders, garden, pool, pet or firewood supplier. You review and correct every item before saving.')}</p>
        <div className="mx-6 mt-4 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-[12px] text-slate-600 leading-relaxed text-left" data-testid="local-receipt-privacy">
          {t('local.receiptPrivacy', 'Receipt Privacy: Your receipt is used only to read product and price information. The receipt image is not displayed publicly and is not permanently stored by Home & Local Prices. Personal and payment information is not added to the price database.')}
        </div>
        <div className="flex flex-col gap-2 mt-4 px-6">
          <button onClick={() => !busy && camRef.current?.click()} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold bg-indigo-600 text-white disabled:opacity-50" data-testid="local-take-photo">
            <Camera className="h-4 w-4" />{busy ? t('local.reading', 'Reading…') : t('local.takePhoto', 'Take Photo')}
          </button>
          <button onClick={() => !busy && galRef.current?.click()} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border border-indigo-300 text-indigo-700 bg-white disabled:opacity-50" data-testid="local-choose-photo">
            <ImageIcon className="h-4 w-4" />{t('local.choosePhoto', 'Choose Existing Photo')}
          </button>
          <input ref={camRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleFile(f); }} data-testid="local-camera-input" />
          <input ref={galRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleFile(f); }} data-testid="local-gallery-input" />
        </div>
        {preview && <img src={preview} alt="receipt" className="mx-auto max-h-48 rounded-lg border mt-4 object-contain" data-testid="local-scan-preview" />}
        <p className="text-[10px] text-gray-400 mt-4 px-6">{t('local.photoNotStored', 'The photo is used only to read the prices and is never stored.')}</p>
      </div>
    );

    return (
      <div className="pb-6" data-testid="local-scan-review">
        <div className="bg-white border rounded-2xl p-3 mb-3">
          <p className="text-[12px] font-bold text-slate-600 mb-2">{t('local.scanReceiptInfo', 'Receipt details')}</p>
          <div className="grid grid-cols-2 gap-2">
            <Input value={sform.shopName} onChange={(e) => setSform((p) => ({ ...p, shopName: e.target.value }))} placeholder={t('local.fShop', 'Shop / Supplier')} data-testid="local-scan-shop" />
            <select value={sform.town} onChange={(e) => setSform((p) => ({ ...p, town: e.target.value }))} className="h-10 px-2 rounded-md border bg-white text-sm" data-testid="local-scan-town">
              <option value="">{t('local.fTown', 'Town')}</option>{towns.map((tn) => <option key={tn} value={tn}>{tn}</option>)}
            </select>
            <Input type="date" value={sform.observedDate} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setSform((p) => ({ ...p, observedDate: e.target.value }))} data-testid="local-scan-date" />
          </div>
        </div>
        {anyReview && (
          <div className="flex items-center gap-2 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-2" data-testid="local-scan-review-warn">
            <AlertTriangle className="h-4 w-4 shrink-0" />{t('local.scanReviewWarn', 'Some items need a category, unit or price before they can be saved.')}
          </div>
        )}
        {sform.items.map((it, i) => {
          const subs = cat(it.categoryId)?.subs || [];
          const uOpts = subs.find((s) => s.id === it.subCategoryId)?.units || [];
          return (
            <div key={i} className={`bg-white border rounded-2xl p-3 mb-2.5 ${it.needsReview ? 'border-amber-400' : 'border-gray-200'}`} data-testid={`local-scan-item-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <Input value={it.productName} onChange={(e) => setItem(i, 'productName', e.target.value)} className="flex-1" data-testid={`local-scan-item-${i}-name`} />
                <button onClick={() => removeItem(i)} className="p-2 text-rose-500" data-testid={`local-scan-item-${i}-delete`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={it.categoryId} onChange={(e) => setItem(i, 'categoryId', e.target.value)} className={selCls} data-testid={`local-scan-item-${i}-category`}>
                  <option value="">{t('local.fCategory', 'Category')}…</option>{taxonomy.map((c) => <option key={c.id} value={c.id}>{cl(c.id)}</option>)}
                </select>
                <select value={it.subCategoryId} onChange={(e) => setItem(i, 'subCategoryId', e.target.value)} className={selCls} data-testid={`local-scan-item-${i}-subcategory`}>
                  <option value="">{t('local.fSubcategory', 'Subcategory')}…</option>{subs.map((s) => <option key={s.id} value={s.id}>{sl(s.id)}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <div><span className="text-[10px] text-slate-400">{t('local.fPrice', 'Price (€)')}</span><Input type="number" step="0.01" value={it.price} onChange={(e) => setItem(i, 'price', e.target.value)} data-testid={`local-scan-item-${i}-price`} /></div>
                <div><span className="text-[10px] text-slate-400">{t('local.fUnit', 'Unit')}</span>
                  <select value={it.unit} onChange={(e) => setItem(i, 'unit', e.target.value)} className={selCls} data-testid={`local-scan-item-${i}-unit`}>
                    <option value="">…</option>{uOpts.map((u) => <option key={u} value={u}>{ul(u)}</option>)}
                  </select></div>
                <div><span className="text-[10px] text-slate-400">{t('local.fQty', 'Qty')}</span><Input type="number" step="0.01" value={it.quantity} onChange={(e) => setItem(i, 'quantity', e.target.value)} data-testid={`local-scan-item-${i}-qty`} /></div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <div><span className="text-[10px] text-slate-400">{t('local.packContainsShort', 'Pack contents')}</span><Input type="number" step="0.01" min="0" value={it.packContainsMag} onChange={(e) => setItem(i, 'packContainsMag', e.target.value)} placeholder="25" data-testid={`local-scan-item-${i}-packmag`} /></div>
                <div><span className="text-[10px] text-slate-400">{t('local.fPackUnit', 'of unit')}</span>
                  <select value={it.packContainsUnit} onChange={(e) => setItem(i, 'packContainsUnit', e.target.value)} className={selCls} data-testid={`local-scan-item-${i}-packunit`}>
                    <option value="">—</option>{['kg', 'g', 'litre', 'ml', 'm2', 'm3'].map((u) => <option key={u} value={u}>{ul(u)}</option>)}
                  </select></div>
                <div><span className="text-[10px] text-slate-400">{t('local.fBrand', 'Brand')}</span><Input value={it.brand || ''} onChange={(e) => setItem(i, 'brand', e.target.value)} data-testid={`local-scan-item-${i}-brand`} /></div>
              </div>
              {parseFloat(it.price) > 0 && parseFloat(it.quantity) > 0 && it.unit && (
                <div className="text-[11px] font-semibold text-indigo-700 mt-1.5" data-testid={`local-scan-item-${i}-perunit`}>
                  ≈ {money(parseFloat(it.price) / parseFloat(it.quantity), sform.currency)} / {ul(it.unit)} {t('local.each', 'each')}
                  {parseFloat(it.packContainsMag) > 0 && it.packContainsUnit ? ` · ${money(parseFloat(it.price) / (parseFloat(it.packContainsMag) * parseFloat(it.quantity || 1)), sform.currency)}/${ul(it.packContainsUnit)}` : ''}
                </div>
              )}
            </div>
          );
        })}
        <Button className="w-full mt-2 h-11 bg-indigo-600 hover:bg-indigo-700" onClick={save} disabled={saving} data-testid="local-scan-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('local.confirmSave', 'Confirm & Save')}
        </Button>
        <p className="text-[10px] text-gray-400 mt-2">{t('local.photoNotStored', 'The photo is used only to read the prices and is never stored.')}</p>
      </div>
    );
  };

  // ---------- headers ----------
  const back = () => {
    if (view === 'subcats') setView('landing');
    else if (view === 'results') setView(activeSub ? 'subcats' : 'landing');
    else if (view === 'detail') setView('results');
    else setView('landing');
  };
  const title = view === 'landing' ? t('local.title', 'Home & Local Prices')
    : view === 'subcats' ? cl(activeCat?.id)
    : view === 'results' ? sl(activeSub?.id)
    : view === 'add' ? t('local.addPrice', 'Add a Price')
    : view === 'search' ? t('local.searchTitle', 'Search Local Prices')
    : view === 'scan' ? t('local.scanTab', 'Scan Receipt')
    : detail ? detail.shopName : '';

  return (
    <div className="min-h-screen bg-slate-50 pb-24" dir={i18n.dir()}>
      <header className="bg-indigo-600 text-white px-4 pt-4 pb-4 rounded-b-3xl shadow-lg">
        <div className="flex items-center gap-2">
          {view === 'landing'
            ? <button onClick={() => navigate('/')} className="p-1.5 -ml-1 rounded-full hover:bg-white/15" data-testid="local-home-back"><ArrowLeft className="h-5 w-5" /></button>
            : <button onClick={back} className="p-1.5 -ml-1 rounded-full hover:bg-white/15" data-testid="local-back"><ArrowLeft className="h-5 w-5" /></button>}
          <div className="min-w-0">
            <h1 className="text-lg font-black leading-tight truncate">{title}</h1>
            {view === 'landing' && <p className="text-[11px] text-white/80">{t('local.tagline', 'What do I need, where, how much, how far, how recent?')}</p>}
          </div>
        </div>
        {view === 'landing' && (
          <button onClick={() => navigate('/price')} className="mt-3 text-[11px] bg-white/15 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5" data-testid="local-to-grocery">
            🛒 {t('local.separateBanner', 'Separate from Food Shopping (Price-2-Day)')} <ChevronRight className="h-3 w-3" />
          </button>
        )}
      </header>

      <main className="px-4 pt-4 max-w-xl mx-auto">
        {view === 'landing' && (
          <div className="grid grid-cols-2 gap-3" data-testid="local-category-grid">
            {taxonomy.map((c) => (
              <button key={c.id} onClick={() => openCategory(c)} className="bg-white border border-gray-200 rounded-2xl p-4 text-center shadow-sm active:scale-[.98] transition-transform" data-testid={`local-cat-${c.id}`}>
                <div className="text-3xl">{c.icon}</div>
                <div className="text-[13px] font-bold text-slate-800 mt-2 leading-tight">{cl(c.id)}</div>
              </button>
            ))}
          </div>
        )}

        {view === 'subcats' && (
          <div data-testid="local-subcat-list">
            {(activeCat?.subs || []).map((s) => (
              <button key={s.id} onClick={() => openSub(s)} className="w-full bg-white border border-gray-200 rounded-2xl p-3.5 mb-2.5 flex items-center justify-between" data-testid={`local-sub-${s.id}`}>
                <span className="font-semibold text-slate-800 text-sm">{sl(s.id)}</span>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </button>
            ))}
          </div>
        )}

        {view === 'results' && <ResultsBody />}

        {view === 'search' && (
          <div data-testid="local-search-view">
            <div className="flex gap-2 mb-3">
              <div className="flex-1 flex items-center gap-2 bg-white border rounded-full px-4 h-11">
                <Search className="h-4 w-4 text-gray-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && runSearch()} placeholder={t('local.searchPh', 'e.g. logs, diesel, tiles, cement')} className="flex-1 bg-transparent outline-none text-sm" data-testid="local-search-input" />
              </div>
              <Button onClick={runSearch} className="h-11 bg-indigo-600" data-testid="local-search-button">{t('local.searchBtn', 'Search')}</Button>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {['logs', 'diesel', 'tiles', 'cement', 'chlorine'].map((s) => (
                <button key={s} onClick={() => { setQ(s); setSearched(true); setMode(town ? 'nearest' : 'recent'); fetchRows({ q: s, sort: town ? 'nearest' : 'recent' }); }} className="text-[12px] bg-white border rounded-full px-3 py-1.5 text-slate-600" data-testid={`local-suggest-${s}`}>{s}</button>
              ))}
            </div>
            {searched && <ResultsBody />}
          </div>
        )}

        {view === 'add' && <AddForm />}

        {view === 'scan' && <ScanFlow />}

        {view === 'detail' && detail && (
          <div data-testid="local-detail">
            {editing ? <EditForm /> : (
            <div className="bg-white border rounded-2xl p-4">
              <div className="text-2xl font-black text-indigo-600">{money(detail.pricePerUnit, detail.currency)} <span className="text-sm font-bold text-slate-500">{t('local.per', 'per')} {ul(detail.unit)}</span></div>
              <dl className="mt-2 text-sm">
                <dt className="text-[11px] text-slate-500 mt-2">{t('local.dProduct', 'Product')}</dt><dd className="font-bold text-slate-900">{detail.productName}{detail.grade ? ` · ${detail.grade}` : ''}</dd>
                <dt className="text-[11px] text-slate-500 mt-2">{t('local.dShop', 'Supplier / shop')}</dt><dd className="font-bold text-slate-900">{detail.shopName}</dd>
                <dt className="text-[11px] text-slate-500 mt-2">{t('local.dTown', 'Town / location')}</dt><dd className="font-bold text-slate-900">{detail.town}</dd>
                {detail.distanceKm != null && <><dt className="text-[11px] text-slate-500 mt-2">{t('local.dDistance', 'Approx. distance')}</dt><dd className="font-bold text-slate-900">~{detail.distanceKm} km <span className="text-[11px] font-normal text-slate-400">({t('local.straightLine', 'straight-line, not driving')})</span></dd></>}
                <dt className="text-[11px] text-slate-500 mt-2">{t('local.dRecorded', 'Price recorded')}</dt><dd className="font-bold text-slate-900">{detail.observedDate} · <span className={`text-[10px] rounded-full px-2 py-0.5 ${freshBadge(detail.freshness).cls}`}>{t(`local.fresh.${(detail.freshness?.label || 'Unknown').toLowerCase()}`, detail.freshness?.label)}</span></dd>
                {detail.includesDelivery != null && <><dt className="text-[11px] text-slate-500 mt-2">{t('local.dDelivery', 'Delivery')}</dt><dd className="font-bold text-slate-900">{detail.includesDelivery ? t('local.included', 'Included') : t('local.notIncluded', 'Not included')}</dd></>}
                {detail.note && <><dt className="text-[11px] text-slate-500 mt-2">{t('local.dNote', 'Note')}</dt><dd className="text-slate-700 whitespace-pre-wrap break-words" data-testid="local-detail-note">{detail.note}</dd></>}
              </dl>
              <div className="mt-3">
                <PriceTrend
                  observations={history}
                  loading={historyLoading}
                  money={money}
                  labels={{
                    title: t('local.historyTitle', 'Price history'),
                    none: t('local.historyNone', 'No price history yet'),
                    noneHint: t('local.historyHint', 'More recorded prices for this product at this supplier will build a trend here.'),
                    latest: t('local.historyLatest', 'LATEST'),
                    rose: t('local.historyRose', 'Price rose'),
                    fell: t('local.historyFell', 'Price fell'),
                    flat: t('local.historyFlat', 'About the same'),
                  }}
                />
              </div>
              {detail.canEdit && (
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => setEditing(true)} className="flex-1 h-11 bg-indigo-600 hover:bg-indigo-700" data-testid="local-detail-edit"><Pencil className="h-4 w-4 mr-1" />{t('local.editPrice', 'Edit price')}</Button>
                  <Button onClick={() => setConfirmDel(true)} variant="outline" className="h-11 border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="local-detail-delete"><Trash2 className="h-4 w-4 mr-1" />{t('local.delete', 'Delete')}</Button>
                </div>
              )}
              {confirmDel && (
                <div className="rounded-xl bg-rose-50 border border-rose-100 p-3 mt-3 space-y-2" data-testid="local-delete-confirm">
                  <p className="text-sm text-rose-700 font-medium">{t('local.confirmDelete', 'Delete this price permanently? This cannot be undone.')}</p>
                  <div className="flex gap-2">
                    <Button onClick={doDeleteDetail} disabled={busyDel} className="flex-1 h-10 bg-rose-600 hover:bg-rose-700" data-testid="local-delete-yes">{busyDel ? <Loader2 className="h-4 w-4 animate-spin" /> : t('local.confirmYes', 'Yes, delete')}</Button>
                    <Button onClick={() => setConfirmDel(false)} variant="outline" className="h-10" data-testid="local-delete-no">{t('local.cancel', 'Cancel')}</Button>
                  </div>
                </div>
              )}
              <a href={dirUrl(detail)} target="_blank" rel="noopener noreferrer" className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-indigo-600 text-white font-bold rounded-xl h-11" data-testid="local-detail-directions"><Navigation className="h-4 w-4" />{t('local.directions', 'Directions')}</a>
              <p className="text-[10px] text-gray-400 mt-3 leading-relaxed">{t('local.disclaimer', 'Prices are community-provided and may change or vary between suppliers. Always check the current price before buying.')}</p>
            </div>
            )}
          </div>
        )}
      </main>

      {/* bottom tabs */}
      <nav className="fixed bottom-0 inset-x-0 bg-white border-t flex max-w-xl mx-auto" data-testid="local-tabbar">
        {[['landing', Home, t('local.tabBrowse', 'Browse')], ['search', Search, t('local.tabSearch', 'Search')], ['scan', ScanLine, t('local.scanTab', 'Scan Receipt')], ['add', Plus, t('local.tabAdd', 'Add Price')]].map(([k, Icon, lab]) => (
          <button key={k} onClick={() => { if (k === 'add') setView('add'); else if (k === 'search') setView('search'); else if (k === 'scan') setView('scan'); else setView('landing'); }}
            className={`flex-1 py-2.5 flex flex-col items-center gap-0.5 ${(view === k || (k === 'landing' && ['subcats', 'results', 'detail'].includes(view))) ? 'text-indigo-600' : 'text-gray-400'}`} data-testid={`local-tab-${k}`}>
            <Icon className="h-5 w-5" /><span className="text-[10px] font-semibold">{lab}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
